"use client"

import { useState, useMemo } from "react"
import { Search, Plus, Users, DollarSign, Clock, CheckCircle, Eye } from "lucide-react"
import ServiceRequestModal from "./ServiceRequestModal"
import ApplicantsModal from "./ApplicantsModal"

const ServiceBidsTab = ({
  event,
  serviceRequests,
  setServiceRequests,
  isCreator,
  user,
  fetchServiceRequests,
  showSuccessModal,
  showFailureModal,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [isApplicantsModalOpen, setIsApplicantsModalOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)

  const itemsPerPage = 9

  const filteredRequests = useMemo(() => {
    return serviceRequests.filter(
      (request) =>
        request.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [serviceRequests, searchTerm])

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedRequests = filteredRequests.slice(startIndex, startIndex + itemsPerPage)

  const handleViewApplicants = (request) => {
    setSelectedRequest(request)
    setIsApplicantsModalOpen(true)
  }

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Open for Bids",
        icon: Clock,
      },
      pending_completion_confirmation: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        label: "Awaiting Confirmation",
        icon: Clock,
      },
      completed: {
        bg: "bg-green-100",
        text: "text-green-800",
        label: "Completed",
        icon: CheckCircle,
      },
    }
    return configs[status] || configs.pending
  }

  const getApplicantCount = (serviceProviders) => {
    if (Array.isArray(serviceProviders)) {
      return serviceProviders.length
    }
    if (typeof serviceProviders === "string") {
      return serviceProviders.split(",").filter((p) => p.trim() !== "").length
    }
    return 0
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="text-purple-600" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Service Requests</h2>
              <p className="text-gray-600">Manage service requests and bids for your event</p>
            </div>
          </div>
          {isCreator && (
            <button
              onClick={() => setIsRequestModalOpen(true)}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
            >
              <Plus size={18} />
              Request Service
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search service requests..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setCurrentPage(1)
            }}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Service Requests Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedRequests.length > 0 ? (
          paginatedRequests.map((request) => {
            const statusConfig = getStatusConfig(request.status)
            const StatusIcon = statusConfig.icon
            const applicantCount = getApplicantCount(request.service_providers)

            return (
              <div
                key={request.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{request.title}</h3>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                    >
                      <StatusIcon size={12} />
                      {statusConfig.label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{request.description}</p>

                  {/* Stats */}
                  <div className="flex items-center justify-between mb-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Users size={16} />
                      <span>
                        {applicantCount} applicant{applicantCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-purple-600 font-semibold">
                      <DollarSign size={16} />
                      <span>KSh {request.price?.toLocaleString() || request.budget?.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Deadline */}
                  {request.deadline && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-4">
                      <Clock size={12} />
                      <span>Due: {new Date(request.deadline).toLocaleDateString()}</span>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={() => handleViewApplicants(request)}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      request.status === "pending_completion_confirmation"
                        ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                        : request.status === "completed"
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    <Eye size={16} />
                    {request.status === "pending_completion_confirmation"
                      ? "Confirm Completion"
                      : request.status === "completed"
                        ? "View Provider"
                        : "View Applicants"}
                  </button>
                </div>
              </div>
            )
          })
        ) : (
          <div className="col-span-full">
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <DollarSign size={64} className="mx-auto mb-6 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Service Requests</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? "No requests match your search criteria."
                  : "Start by creating your first service request."}
              </p>
              {isCreator && !searchTerm && (
                <button
                  onClick={() => setIsRequestModalOpen(true)}
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
                >
                  <Plus size={18} />
                  Create Service Request
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredRequests.length)} of{" "}
              {filteredRequests.length} results
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm font-medium text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ServiceRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        event={event}
        fetchServiceRequests={fetchServiceRequests}
        showSuccessModal={showSuccessModal}
        showFailureModal={showFailureModal}
      />

      <ApplicantsModal
        isOpen={isApplicantsModalOpen}
        onClose={() => setIsApplicantsModalOpen(false)}
        request={selectedRequest}
        showSuccessModal={showSuccessModal}
        showFailureModal={showFailureModal}
      />
    </div>
  )
}

export default ServiceBidsTab
